import _ from 'lodash';
import {
  useFields,
  useWizardService,
  useCurrentStepIndex,
  ComparatorContext,
  GraphComparisonApiService,
  GraphLegacyApiService,
} from '@kizi/comparator-core';
import { sendCustomEvent } from '../components/wrappers/TabViewWizard/ApplyPageWizard';
import { onClick } from './product';
import { useContext } from 'react';

function combineStrings(items: string[], lastSeparator = 'en'): string {
  let newString = '';

  if (items.length === 1) {
    newString = items[0];
  } else {
    newString =
      items.slice(0, items.length - 1).join(', ') +
      ` ${lastSeparator} ` +
      items[items.length - 1];
  }

  return newString;
}

function formatPrice(price: string | number | null, decimals = 2): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(price) || 0);
}

function getIconLabel(text: string): string {
  switch (text) {
    case 'bio':
      return 'biomass';
    case 'kern':
      return 'nuclear';
    case 'kolen':
      return 'coal';
    case 'overig':
      return 'import';
    case 'water':
      return 'water-dam';
    case 'zon':
      return 'sun-2';
    default:
      return text;
  }
}

function getRandomNumber(): number {
  return self.crypto.getRandomValues(new Uint32Array(1))[0];
}

function sortArrayByKey(array, key, descending = false) {
  const sortedArray: any[] = _.sortBy(array, key);

  if (descending) return sortedArray.reverse();
  else return sortedArray;
}

function scrollToTop(): void {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
}

const isLargeDesktop = (): boolean => {
  if (window.matchMedia('(min-width: 1280px)').matches) {
    // The viewport is more than 1280 pixels wide
    return true;
  } else {
    return false;
  }
};

const isMobile = (): boolean => {
  if (window.matchMedia('(max-width: 767px)').matches) {
    // The viewport is less than 768 pixels wide
    return true;
  } else {
    return false;
  }
};

const isMobileXs = (): boolean => {
  if (window.matchMedia('(max-width: 425px)').matches) {
    // The viewport is less than 768 pixels wide
    return true;
  } else {
    return false;
  }
};

const isEmailValid = (email: string): boolean => {
  const regex =
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
  return regex.test(email);
};

const isEmpty = (value: string): boolean => {
  return value.length === 0;
};

const generateUUID = (): string => {
  let d = new Date().getTime(); //Timestamp
  let d2 =
    (typeof performance !== 'undefined' &&
      performance.now &&
      performance.now() * 1000) ||
    0; //Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = self.crypto.getRandomValues(new Uint32Array(1))[0] % 17;
    if (d > 0) {
      //Use timestamp until depleted
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      //Use microseconds since page-load if supported
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const getCalculateSessionId = (): string => {
  let calculateSessionID = localStorage.getItem('calculateSessionId') ?? '';

  if (!calculateSessionID) {
    calculateSessionID = generateUUID();
    localStorage.setItem('calculateSessionId', calculateSessionID);
  }

  return calculateSessionID;
};

const useCoveragesWithValue = (checkedCoverages) => {
  let coverageadditionsValue: any = [];
  if (checkedCoverages && checkedCoverages.value !== undefined) {
    coverageadditionsValue = checkedCoverages.value;
  }

  const [...checkedCoveragesFields] = useFields(coverageadditionsValue);

  return checkedCoveragesFields.filter((coverage) => Boolean(coverage?.value));
};

const useHandleSubmitClick = (
  shouldOpenNewTab,
  data,
  nextIndex,
  registerAnalyticsAddToCartEvent?,
  registerInPeregrineEvent?,
  checkoutInPeregrineEvent?,
) => {
  const service = useWizardService();
  const currentStepIndex = useCurrentStepIndex();
  const { api } = useContext(ComparatorContext);

  if (!service?.state?.context?.steps || currentStepIndex === undefined)
    return () => undefined;

  const interpreter = service.state.context.steps[currentStepIndex];

  if (shouldOpenNewTab) {
    return () => {
      sendCustomEvent('affiliate-apply', {
        selectedProduct: data,
      });
      service.send([
        {
          type: 'SET_FORM_FIELDS',
          name: interpreter.id,
          fields: [{ name: 'selectedProduct', value: JSON.stringify(data) }],
        },
      ]);
      if (registerAnalyticsAddToCartEvent) {
        registerAnalyticsAddToCartEvent(data);
      }
      if (registerInPeregrineEvent) {
        const allFields = [
          ...(interpreter?.getSnapshot()?.context.externalFields || []),
          ...(interpreter?.getSnapshot()?.context.fields || []),
        ];

        try {
          const graphApi =
            api as GraphComparisonApiService<GraphLegacyApiService>;
          const p1 = graphApi.getPeregrineUserHash(allFields);
          const p2 = graphApi.getPeregrinePartnerHash(allFields);
          const p3 = graphApi.getPeregrineActiveExperiments(allFields);

          Promise.all([p1, p2, p3]).then(
            ([userHash, partnerHash, activeExperiments]) => {
              const eventData = registerInPeregrineEvent(
                allFields,
                userHash,
                partnerHash,
                activeExperiments,
              );
              const checkoutEventData = checkoutInPeregrineEvent(
                allFields,
                userHash,
                partnerHash,
                activeExperiments,
                'referral',
              );
              const p3 = graphApi.sendPeregrineEvent(eventData);
              const p4 = graphApi.sendPeregrineEvent(checkoutEventData);

              Promise.all([p3, p4]);
            },
          );
        } catch (e) {
          console.log(e);
        }
      }

      window.open(`${location.href}&affiliateUrl`);
    };
  }

  return () =>
    onClick(
      interpreter,
      service,
      data,
      nextIndex,
      registerAnalyticsAddToCartEvent,
      registerInPeregrineEvent,
      api,
    );
};

const sanitizeObject = (object) => {
  Object.entries(object).forEach(([k, v]) => {
    if (v && typeof v === 'object') sanitizeObject(v);

    if (
      (v && typeof v === 'object' && !Object.keys(v).length) ||
      v === null ||
      v === undefined ||
      // @ts-ignore: Unreachable code error
      v.length === 0
    ) {
      // @ts-ignore: Unreachable code error
      if (Array.isArray(object)) object.splice(k, 1);
      else if (!(v instanceof Date)) delete object[k];
    }
  });

  return object;
};

const getCookie = (cookieName) => {
  let cookie = {};
  document.cookie.split(';').forEach((el) => {
    let [key, value] = el.split('=');
    cookie[key.trim()] = value;
  });
  return cookie[cookieName];
};

const setCookie = (name, value, minutes) => {
  const expires = new Date(Date.now() + minutes * 60000).toUTCString();
  document.cookie =
    name +
    '=' +
    encodeURIComponent(value) +
    '; expires=' +
    expires +
    '; path=/';
};

const getAnalyticsSessionIdCookieName = () => {
  return '_ga_Z1X27LTGWF';
};

export {
  isEmailValid,
  combineStrings,
  formatPrice,
  getIconLabel,
  getRandomNumber,
  isLargeDesktop,
  isMobile,
  scrollToTop,
  sortArrayByKey,
  getCalculateSessionId,
  useCoveragesWithValue,
  useHandleSubmitClick,
  sanitizeObject,
  isMobileXs,
  getCookie,
  setCookie,
  generateUUID,
  getAnalyticsSessionIdCookieName,
  isEmpty,
};
